/**
 * Outil « Circuit électrique » (Boîte à outils, physique-chimie 6e → 1re).
 *
 * Explorer : un montage (une boucle, série, dérivation, mixte en 2nde), un schéma normalisé où
 * le courant circule (points qui avancent dans le sens conventionnel, plus vite là où
 * l'intensité est plus grande), des lampes qui brillent selon leur puissance. On ouvre ou ferme
 * l'interrupteur (aussi en touchant K sur le schéma), on retire ou court-circuite un dipôle, on
 * règle la tension du générateur, les résistances (dès la 4e), la résistance interne (1re), et
 * on peut court-circuiter le générateur. Mesures (dès la 4e, ou à la demande) : U, I, P de
 * chaque dipôle ; les lois vérifiées sont écrites dessous. Défi : cinq questions selon le
 * niveau. Logique : `circuit.ts`.
 */

import { useMemo, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  CIRCUITS,
  GLOW_TEXT,
  RESISTORS,
  amps,
  circuitById,
  circuitsFor,
  dipoleName,
  fr,
  glowOf,
  lawLines,
  makeQuiz,
  ohms,
  quizKinds,
  solveCircuit,
  volts,
  watts,
  type CircuitId,
  type CircuitInput,
  type CircuitResult,
  type DipoleId,
  type DipoleKind,
  type DipoleState,
  type Glow,
} from './circuit';

const W = 340;
const H = 250;
const TOP = 40;
const BOT = 220;
const GX = 40;
const WIRE = 'var(--color-ink-700)';
const FLOW = 'var(--color-primary-500)';
const DANGER = 'var(--color-error-600)';
const LAMP_LIGHT = '#facc15';

type Cur = 'main' | DipoleId;
interface Seg {
  d: string;
  cur: Cur;
}
interface Slot {
  id: DipoleId;
  x: number;
  y: number;
  orient: 'h' | 'v';
}

/** fils (dans le sens conventionnel du courant : du + vers le −) et emplacements des dipôles */
const LAYOUT: Record<CircuitId, { segs: Seg[]; slots: Slot[]; nodes: Array<[number, number]> }> = {
  loop: {
    segs: [
      { d: `M${GX} 111 V${TOP} H72`, cur: 'main' },
      { d: `M112 ${TOP} H300 V110`, cur: 'main' },
      { d: `M300 150 V${BOT} H${GX} V139`, cur: 'main' },
    ],
    slots: [{ id: 'D1', x: 300, y: 130, orient: 'v' }],
    nodes: [],
  },
  series: {
    segs: [
      { d: `M${GX} 111 V${TOP} H72`, cur: 'main' },
      { d: `M112 ${TOP} H190`, cur: 'main' },
      { d: `M230 ${TOP} H300 V110`, cur: 'main' },
      { d: `M300 150 V${BOT} H${GX} V139`, cur: 'main' },
    ],
    slots: [
      { id: 'D1', x: 210, y: TOP, orient: 'h' },
      { id: 'D2', x: 300, y: 130, orient: 'v' },
    ],
    nodes: [],
  },
  parallel: {
    segs: [
      { d: `M${GX} 111 V${TOP} H72`, cur: 'main' },
      { d: `M112 ${TOP} H200`, cur: 'main' },
      { d: `M200 ${TOP} V110`, cur: 'D1' },
      { d: `M200 150 V${BOT}`, cur: 'D1' },
      { d: `M200 ${TOP} H300 V110`, cur: 'D2' },
      { d: `M300 150 V${BOT} H200`, cur: 'D2' },
      { d: `M200 ${BOT} H${GX} V139`, cur: 'main' },
    ],
    slots: [
      { id: 'D1', x: 200, y: 130, orient: 'v' },
      { id: 'D2', x: 300, y: 130, orient: 'v' },
    ],
    nodes: [
      [200, TOP],
      [200, BOT],
    ],
  },
  mixed: {
    segs: [
      { d: `M${GX} 111 V${TOP} H72`, cur: 'main' },
      { d: `M112 ${TOP} H130`, cur: 'main' },
      { d: `M170 ${TOP} H220`, cur: 'main' },
      { d: `M220 ${TOP} V110`, cur: 'D2' },
      { d: `M220 150 V${BOT}`, cur: 'D2' },
      { d: `M220 ${TOP} H300 V110`, cur: 'D3' },
      { d: `M300 150 V${BOT} H220`, cur: 'D3' },
      { d: `M220 ${BOT} H${GX} V139`, cur: 'main' },
    ],
    slots: [
      { id: 'D1', x: 150, y: TOP, orient: 'h' },
      { id: 'D2', x: 220, y: 130, orient: 'v' },
      { id: 'D3', x: 300, y: 130, orient: 'v' },
    ],
    nodes: [
      [220, TOP],
      [220, BOT],
    ],
  },
};

/** intensité dans une portion du circuit (fil qui court-circuite un dipôle compris) */
function currentOf(res: CircuitResult, cur: Cur): number {
  if (cur === 'main') return res.I;
  const d = res.dipoles[cur];
  return d.I + d.Ishort;
}

/** durée d'un tour d'animation : plus l'intensité est grande, plus les points vont vite */
function flowDuration(I: number, Iref: number): number {
  if (!Number.isFinite(I)) return 0.25;
  return Math.min(4, Math.max(0.35, (1.2 * Iref) / I));
}

function Flow({ d, I, Iref, danger }: { d: string; I: number; Iref: number; danger: boolean }) {
  if (!(I > 1e-9)) return null;
  return (
    <path
      d={d}
      className="cc-flow"
      fill="none"
      stroke={danger ? DANGER : FLOW}
      strokeWidth={3.5}
      strokeLinecap="round"
      style={{ ['--cc-dur' as string]: `${flowDuration(I, Iref)}s` }}
    />
  );
}

/** court-circuit d'un dipôle : un fil qui le contourne */
function bypassPath(s: Slot): string {
  return s.orient === 'v'
    ? `M${s.x} 102 H${s.x + 22} V158 H${s.x}`
    : `M${s.x - 28} ${s.y} V${s.y - 18} H${s.x + 28} V${s.y}`;
}

function Dipole({
  slot,
  kind,
  state,
  glow,
  name,
}: {
  slot: Slot;
  kind: DipoleKind;
  state: DipoleState;
  glow: Glow;
  name: string;
}) {
  const { x, y, orient } = slot;
  const removed = state === 'removed';
  const labelX = orient === 'v' ? x - 20 : x;
  const labelY = orient === 'v' ? y + 5 : y + 30;
  const anchor = orient === 'v' ? 'end' : 'middle';
  const glowOpacity = { off: 0, dim: 0.35, normal: 0.85, bright: 1 }[glow];
  return (
    <g data-testid={`cc-dipole-${slot.id}`} data-state={state} data-glow={glow}>
      {/* fils qui arrivent au dipôle */}
      {orient === 'v' ? (
        <path d={`M${x} 110 V116 M${x} 144 V150`} stroke={WIRE} strokeWidth={2} />
      ) : (
        <path
          d={`M${x - 20} ${y} H${x - 16} M${x + 16} ${y} H${x + 20}`}
          stroke={WIRE}
          strokeWidth={2}
        />
      )}
      {kind === 'lamp' ? (
        <g opacity={removed ? 0.35 : 1}>
          {!removed && glow !== 'off' && (
            <circle
              cx={x}
              cy={y}
              r={glow === 'bright' ? 27 : 22}
              fill={LAMP_LIGHT}
              opacity={glowOpacity * 0.35}
            />
          )}
          <circle
            cx={x}
            cy={y}
            r={14}
            fill={!removed && glow !== 'off' ? LAMP_LIGHT : 'var(--fig-bg, #fff)'}
            fillOpacity={!removed && glow !== 'off' ? glowOpacity : 1}
            stroke={WIRE}
            strokeWidth={2}
            strokeDasharray={removed ? '4 3' : undefined}
          />
          <path
            d={`M${x - 10} ${y - 10} L${x + 10} ${y + 10} M${x + 10} ${y - 10} L${x - 10} ${y + 10}`}
            stroke={WIRE}
            strokeWidth={2}
          />
        </g>
      ) : (
        <rect
          x={orient === 'v' ? x - 7 : x - 18}
          y={orient === 'v' ? y - 18 : y - 7}
          width={orient === 'v' ? 14 : 36}
          height={orient === 'v' ? 36 : 14}
          fill="var(--fig-bg, #fff)"
          stroke={WIRE}
          strokeWidth={2}
          strokeDasharray={removed ? '4 3' : undefined}
          opacity={removed ? 0.35 : 1}
        />
      )}
      <HaloText
        x={labelX}
        y={labelY}
        textAnchor={anchor}
        fontSize={14}
        fontWeight={700}
        fill="var(--color-ink-800)"
      >
        {name}
      </HaloText>
      {removed && (
        <HaloText
          x={labelX}
          y={labelY + 16}
          textAnchor={anchor}
          fontSize={11}
          fill="var(--color-ink-500)"
        >
          {kind === 'lamp' ? 'dévissée' : 'retiré'}
        </HaloText>
      )}
    </g>
  );
}

function Schematic({
  input,
  res,
  onToggleSwitch,
}: {
  input: CircuitInput;
  res: CircuitResult;
  onToggleSwitch: () => void;
}) {
  const layout = LAYOUT[input.id];
  const def = circuitById(input.id);
  const short = res.status === 'short';
  const Iref = Number.isFinite(res.I) && res.I > 0 ? res.I : 1;
  const flowing = res.status !== 'open';
  const names = def.dipoles.map((d) => dipoleName(input.kind, d));
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 block w-full max-w-[26rem] select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Schéma du circuit ${def.label.toLowerCase()} : générateur, interrupteur ${
        input.closed ? 'fermé' : 'ouvert'
      }, ${names.join(', ')}.`}
      data-testid="cc-figure"
    >
      <style>{`
        @keyframes cc-flow { to { stroke-dashoffset: -28; } }
        .cc-flow { stroke-dasharray: 0.1 13.9; animation: cc-flow var(--cc-dur, 1.2s) linear infinite; }
        @media (prefers-reduced-motion: reduce) { .cc-flow { animation: none; } }
      `}</style>
      {/* fils */}
      {layout.segs.map((s) => (
        <path key={s.d} d={s.d} fill="none" stroke={WIRE} strokeWidth={2} />
      ))}
      {layout.nodes.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={4} fill={WIRE} />
      ))}
      {/* générateur : + en haut (grand trait), − en bas (petit trait épais) */}
      <g data-testid="cc-generator">
        <path d={`M${GX - 15} 118 H${GX + 15}`} stroke={WIRE} strokeWidth={2.5} />
        <path d={`M${GX - 8} 132 H${GX + 8}`} stroke={WIRE} strokeWidth={5} />
        <text x={GX - 22} y={114} fontSize={13} fontWeight={700} fill="var(--color-ink-700)">
          +
        </text>
        <text x={GX - 21} y={146} fontSize={13} fontWeight={700} fill="var(--color-ink-700)">
          −
        </text>
        <HaloText x={GX + 22} y={130} fontSize={13} fontWeight={700} fill="var(--color-ink-800)">
          {volts(input.E)}
        </HaloText>
      </g>
      {input.shortGen && (
        <path
          d={`M${GX} 98 H14 V152 H${GX}`}
          fill="none"
          stroke={DANGER}
          strokeWidth={2.5}
          data-testid="cc-short-wire"
        />
      )}
      {/* interrupteur K : on le touche pour l'ouvrir ou le fermer */}
      <g
        role="button"
        tabIndex={0}
        aria-label={input.closed ? 'Ouvrir l’interrupteur K' : 'Fermer l’interrupteur K'}
        aria-pressed={input.closed}
        onClick={onToggleSwitch}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleSwitch();
          }
        }}
        className="cursor-pointer outline-none"
        data-testid="cc-switch"
      >
        <rect x={64} y={10} width={56} height={44} fill="transparent" />
        <circle cx={72} cy={TOP} r={3} fill={WIRE} />
        <circle cx={112} cy={TOP} r={3} fill={WIRE} />
        <path
          d={input.closed ? `M72 ${TOP} H112` : `M72 ${TOP} L106 ${TOP - 18}`}
          stroke={WIRE}
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <HaloText
          x={92}
          y={TOP + 20}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill="var(--color-ink-800)"
        >
          K
        </HaloText>
      </g>
      {/* dipôles */}
      {layout.slots.map((s) => (
        <Dipole
          key={s.id}
          slot={s}
          kind={input.kind}
          state={input.state[s.id]}
          glow={input.kind === 'lamp' ? glowOf(res.dipoles[s.id].P) : 'off'}
          name={dipoleName(input.kind, s.id)}
        />
      ))}
      {layout.slots
        .filter((s) => input.state[s.id] === 'shorted')
        .map((s) => (
          <path
            key={`by-${s.id}`}
            d={bypassPath(s)}
            fill="none"
            stroke="var(--color-accent-600)"
            strokeWidth={2}
            data-testid={`cc-bypass-${s.id}`}
          />
        ))}
      {/* courant */}
      {flowing && (
        <g aria-hidden="true">
          {layout.segs.map((s) => (
            <Flow key={`f-${s.d}`} d={s.d} I={currentOf(res, s.cur)} Iref={Iref} danger={short} />
          ))}
          {input.closed && <Flow d={`M72 ${TOP} H112`} I={res.I} Iref={Iref} danger={short} />}
          {short && input.shortGen && (
            <Flow d={`M${GX} 98 H14 V152 H${GX}`} I={res.I} Iref={Iref} danger />
          )}
          {layout.slots
            .filter((s) => input.state[s.id] === 'shorted')
            .map((s) => (
              <Flow
                key={`fb-${s.id}`}
                d={bypassPath(s)}
                I={res.dipoles[s.id].Ishort}
                Iref={Iref}
                danger={short}
              />
            ))}
        </g>
      )}
      {short && (
        <HaloText
          x={W / 2}
          y={H / 2 + 5}
          textAnchor="middle"
          fontSize={15}
          fontWeight={800}
          fill={DANGER}
          data-testid="cc-danger"
        >
          COURT-CIRCUIT !
        </HaloText>
      )}
    </svg>
  );
}

const STATE_OPTIONS = (kind: DipoleKind) =>
  [
    { value: 'ok' as const, label: 'En place' },
    { value: 'removed' as const, label: kind === 'lamp' ? 'Dévissée' : 'Retiré' },
    { value: 'shorted' as const, label: 'Court-circuité' },
  ] as const;

function Readings({
  input,
  res,
  rank,
}: {
  input: CircuitInput;
  res: CircuitResult;
  rank: number | null;
}) {
  const def = circuitById(input.id);
  const power = rank === null || rank >= 4;
  const td = 'px-2 py-1.5 text-right tabular-nums';
  return (
    <table className="text-ink-800 w-full text-sm" data-testid="cc-readings">
      <caption className="sr-only">Mesures</caption>
      <thead>
        <tr className="text-ink-500 text-xs">
          <th className="px-2 py-1 text-left font-semibold">Dipôle</th>
          <th className="px-2 py-1 text-right font-semibold">U</th>
          <th className="px-2 py-1 text-right font-semibold">I</th>
          {power && <th className="px-2 py-1 text-right font-semibold">P</th>}
        </tr>
      </thead>
      <tbody>
        <tr className="border-ink-100 border-t">
          <th scope="row" className="px-2 py-1.5 text-left font-semibold">
            Générateur
          </th>
          <td className={td}>{volts(res.UG)}</td>
          <td className={td}>{amps(res.I)}</td>
          {power && <td className={td}>{watts(res.UG * res.I)}</td>}
        </tr>
        {def.dipoles.map((d) => {
          const r = res.dipoles[d];
          return (
            <tr key={d} className="border-ink-100 border-t">
              <th scope="row" className="px-2 py-1.5 text-left font-semibold">
                {dipoleName(input.kind, d)}
                {input.kind === 'resistor' && (
                  <span className="text-ink-500 ml-1 text-xs font-normal">({ohms(r.R)})</span>
                )}
              </th>
              <td className={td}>{volts(r.U)}</td>
              <td className={td}>{amps(r.I)}</td>
              {power && <td className={td}>{watts(r.P)}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const DEFAULT_R: Record<DipoleId, number> = { D1: 100, D2: 220, D3: 330 };
const ALL_OK: Record<DipoleId, DipoleState> = { D1: 'ok', D2: 'ok', D3: 'ok' };

function Explore({ rank }: { rank: number | null }) {
  const circuits = circuitsFor(rank);
  const collegeStart = rank !== null && rank <= 2;
  const canResistors = rank === null || rank >= 3;
  const realSource = rank === null || rank >= 6;
  const [id, setId] = useState<CircuitId>('series');
  const [kind, setKind] = useState<DipoleKind>(
    rank !== null && rank >= 3 && rank <= 4 ? 'resistor' : 'lamp',
  );
  const [E, setE] = useState(6);
  const [r, setR] = useState(0);
  const [closed, setClosed] = useState(true);
  const [shortGen, setShortGen] = useState(false);
  const [R, setRes] = useState<Record<DipoleId, number>>(DEFAULT_R);
  const [state, setState] = useState<Record<DipoleId, DipoleState>>(ALL_OK);
  const [measures, setMeasures] = useState(!collegeStart);

  const input: CircuitInput = { id, kind, E, r, closed, shortGen, R, state };
  const res = useMemo(() => solveCircuit(input), [id, kind, E, r, closed, shortGen, R, state]); // eslint-disable-line react-hooks/exhaustive-deps
  const lines = lawLines(input, res, rank);
  const def = circuitById(id);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Montage"
        value={id}
        options={circuits.map((c) => ({ value: c.id, label: c.label }))}
        onChange={(v) => {
          setId(v);
          setState(ALL_OK);
          setShortGen(false);
        }}
      />
      <Schematic input={input} res={res} onToggleSwitch={() => setClosed(!closed)} />
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={closed} onClick={() => setClosed(!closed)}>
          {closed ? 'Interrupteur fermé' : 'Interrupteur ouvert'}
        </ToggleChip>
        <ToggleChip on={shortGen} onClick={() => setShortGen(!shortGen)}>
          Fil aux bornes du générateur
        </ToggleChip>
        <ToggleChip on={measures} onClick={() => setMeasures(!measures)}>
          Mesures
        </ToggleChip>
      </div>
      {canResistors && (
        <Segmented
          ariaLabel="Dipôles"
          value={kind}
          options={[
            { value: 'lamp', label: 'Lampes' },
            { value: 'resistor', label: 'Conducteurs ohmiques' },
          ]}
          onChange={setKind}
        />
      )}
      <ParamSlider
        name={realSource ? 'Force électromotrice E' : 'Tension du générateur'}
        ariaLabel="Tension du générateur"
        value={E}
        min={1.5}
        max={12}
        step={0.5}
        onChange={setE}
        format={(v) => volts(v)}
      />
      {realSource && (
        <ParamSlider
          name="Résistance interne r"
          ariaLabel="Résistance interne"
          value={r}
          min={0}
          max={5}
          step={0.5}
          onChange={setR}
          format={(v) => (v === 0 ? '0 Ω (idéal)' : ohms(v))}
        />
      )}
      <div className="space-y-3">
        {def.dipoles.map((d) => (
          <div key={d} className="space-y-2" data-testid={`cc-control-${d}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-ink-900 min-w-8 text-base font-bold">
                {dipoleName(kind, d)}
              </span>
              <Segmented
                ariaLabel={`État de ${dipoleName(kind, d)}`}
                value={state[d]}
                options={STATE_OPTIONS(kind)}
                onChange={(v) => setState({ ...state, [d]: v })}
              />
            </div>
            {kind === 'resistor' && (
              <Stepper
                label={`Résistance ${dipoleName(kind, d)}`}
                value={RESISTORS.indexOf(R[d] as (typeof RESISTORS)[number])}
                min={0}
                max={RESISTORS.length - 1}
                onChange={(i) => setRes({ ...R, [d]: RESISTORS[i]! })}
                format={(i) => ohms(RESISTORS[i]!)}
              />
            )}
          </div>
        ))}
      </div>
      {measures && res.status !== 'short' && <Readings input={input} res={res} rank={rank} />}
      <Note tone={res.status === 'short' ? 'error' : 'neutral'}>
        {lines.map((l) => (
          <span key={l} className="mb-1 block last:mb-0" data-testid="cc-law">
            {frenchSpaces(l)}
          </span>
        ))}
      </Note>
      {kind === 'lamp' && !measures && res.status === 'on' && (
        <p className="sr-only">
          {def.dipoles
            .map((d) => `${dipoleName(kind, d)} ${GLOW_TEXT[glowOf(res.dipoles[d].P)]}`)
            .join(', ')}
        </p>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function CircuitTool({
  level = null,
  seed,
}: {
  /** niveau : montages, mesures et lois selon la classe (6e → 1re) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="cc" />
      </div>
    </div>
  );
}

/** pour les tests : les montages connus */
export const CIRCUIT_IDS = CIRCUITS.map((c) => c.id);
export { fr };
